import { gql, useMutation } from "@apollo/client";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useContext, useState } from "react";
import { ModalContext } from "src/context";
import { MixpanelActions } from "src/services/mixpanel";
import styled from "styled-components";
import { restAPI } from "../../apollo";
import { IIntegrations, IntegrationItem } from "../../types/StateTypes";
import { getIntegrationLabel } from "../../utils/format";
import { theme } from "../../utils/theme";
import { appToast, errorToast } from "../../utils/toast";
import { BACKEND_URL } from "../../utils/variables";
import { OutsideEvent } from "../Dumb";
import { AppText, FlexDiv } from "../UI";
import { CaretDownIcon, MenuSelectContainer, SelectTitle } from "../UI/Phoenix/PhoenixMenuSelect";
import { loggedInUser } from "src/apollo/cache";
import LearnMoreButton from "./IntegrationCard/LearnMoreButton";
import { ConnectButton } from "./IntegrationCard/ConnectButton";
import InfoSection from "./IntegrationCard/InfoSection";

interface SystemConfigIntegrationCardProps {
  integration: IntegrationItem;
  isConnected: boolean;
  disabled?: boolean;
  optionsButton?: React.ReactNode;
  handleGoToManageTemplates?: () => void;
  handleGoToIntegrationReporting?: () => void;
  handleGoToSettingsPage?: () => void;
  handleGoToFieldMapping?: () => void;
  zoomIsEnabledForOrg: boolean;
  googleMeetIsEnabledForOrg: boolean;
}

interface CreateOptionsProps {
  handleGoToManageTemplates?: () => void;
  handleGoToIntegrationReporting?: () => void;
  handleGoToSettingsPage?: () => void;
  handleGoToFieldMapping?: () => void;
  integration: IntegrationItem;
}

const HUBSPOT_SYSTEM_CONFIG_LINK = `${BACKEND_URL}/hubspot/redirect/`;

const ORG_INTEGRATIONS_THAT_NEED_OFF_SITE_PROCESSING = [
  IIntegrations.Zoom,
  IIntegrations.GoogleCalendar,
  IIntegrations.HubSpot,
  IIntegrations.Gmail,
  IIntegrations.JoinMe,
  IIntegrations.Outlook,
  IIntegrations.PandaDoc,
  IIntegrations.Salesforce,
  IIntegrations.Google,
];

const UPDATE_ORG_VIDEO_INTEGRATION = gql`
  mutation UpdateOrgCalendarPreferences($type: CalendarIntegrationType!) {
    updateOrgCalendarPreferences(type: $type)
  }
`;

const linkToSellfireWithCustomURL = async (link: string, linkName: string) => {
  try {
    const api_call = await restAPI
      .get(link)
      .then((response) => {
        if (!!response?.data) {
          const loc = response.data.split("OK. Redirecting to ").join("");
          window.open(loc, "_blank");
        }
      })
      .catch((error) => console.log("error here: ", JSON.stringify(error)));
    return api_call;
  } catch (error: any) {
    appToast(`Error connecting to ${linkName}. Please try again later.`);
  }
};

const defaultLinkToSellfire = async (link?: string | null, organization_id?: string | null) => {
  if (!link || !organization_id) {
    errorToast("Unable to connect to integration. Please contact support.");
    return;
  }

  window.open(`${link}?organization_id=${organization_id}`);
};

const systemConfigLinkGmail = async (url?: string | null) => {
  if (!url) {
    errorToast("Unable to connect to integration. Please contact support.");
    return;
  }

  await linkToSellfireWithCustomURL(url, "Gmail");
};

const systemConfigLinkZoom = async (url?: string | null) => {
  if (!url) {
    errorToast("Unable to connect to integration. Please contact support.");
    return;
  }

  await linkToSellfireWithCustomURL(url, "Zoom");
};

const systemConfigLinkHubSpot = async () => {
  await linkToSellfireWithCustomURL(HUBSPOT_SYSTEM_CONFIG_LINK, "HubSpot");
};

const systemConfigGetTooltipText = ({
  integration,
  switchStateIsConnected,
  zoomIsEnabledForOrg,
  googleMeetIsEnabledForOrg,
  timekitReplacementOrg,
}: {
  integration: IntegrationItem;
  switchStateIsConnected: boolean;
  zoomIsEnabledForOrg: boolean;
  googleMeetIsEnabledForOrg: boolean;
  timekitReplacementOrg: boolean;
}) => {
  if (switchStateIsConnected) return undefined;

  switch (integration.name) {
    case IIntegrations.Zoom:
      return !!googleMeetIsEnabledForOrg && !timekitReplacementOrg
        ? "Zoom is not available when Google Meet is enabled."
        : undefined;
    case IIntegrations.GoogleMeet:
      return !!zoomIsEnabledForOrg && !timekitReplacementOrg
        ? "Google Meet is not available when Zoom is enabled."
        : undefined;
    default:
      return undefined;
  }
};

const systemConfigGetDisabledState = ({
  integration,
  switchStateIsConnected,
  loadingUpdateOrgVideoIntegration,
  timekitReplacementOrg,
  zoomIsEnabledForOrg,
  googleMeetIsEnabledForOrg,
}: {
  integration: IntegrationItem;
  switchStateIsConnected: boolean;
  loadingUpdateOrgVideoIntegration: boolean;
  timekitReplacementOrg: boolean;
  zoomIsEnabledForOrg: boolean;
  googleMeetIsEnabledForOrg: boolean;
}) => {
  switch (integration.name) {
    case "Gmail":
    case IIntegrations.Outlook:
      return integration.disabled || switchStateIsConnected;
    case IIntegrations.Zoom:
      return (
        loadingUpdateOrgVideoIntegration ||
        (!timekitReplacementOrg && !switchStateIsConnected && !!googleMeetIsEnabledForOrg)
      );
    case IIntegrations.GoogleMeet:
      return (
        loadingUpdateOrgVideoIntegration || (!timekitReplacementOrg && !switchStateIsConnected && !!zoomIsEnabledForOrg)
      );
    default:
      return integration.disabled;
  }
};

const CreateSystemConfigIntegrationCardOptions: React.FC<CreateOptionsProps> = ({
  handleGoToManageTemplates,
  handleGoToIntegrationReporting,
  handleGoToSettingsPage,
  handleGoToFieldMapping,
  integration,
}) => {
  const [isClicked, setIsClicked] = useState(false);

  const { hubspotBackfill } = useFlags();

  const options = integration?.options || ({} as any);

  return (
    <OutsideEvent onOutsideClick={() => setIsClicked(false)}>
      <ContainerDiv>
        <MenuSelectContainer
          height={32}
          width={128}
          onClick={() => setIsClicked(!isClicked)}
          style={{
            border: "none",
            backgroundColor: theme.NEUTRAL100,
          }}
        >
          <SelectTitle>More Actions</SelectTitle>
          <CaretDownIcon />
        </MenuSelectContainer>

        {isClicked && (
          <OptionsDiv>
            {options.fieldMapping && (
              <Option onClick={() => handleGoToFieldMapping && handleGoToFieldMapping()}>
                Configure Field Mapping
              </Option>
            )}
            {options.settings && (
              <Option onClick={() => handleGoToSettingsPage && handleGoToSettingsPage()}>Settings</Option>
            )}
            {options.manageTemplates && (
              <Option onClick={() => handleGoToManageTemplates && handleGoToManageTemplates()}>Manage Templates</Option>
            )}
            {options.importMissingLeads && hubspotBackfill && (
              <Option onClick={() => handleGoToManageTemplates && handleGoToManageTemplates()}>
                Import Missing Leads
              </Option>
            )}
            {options.integrationReports && (
              <Option onClick={() => handleGoToIntegrationReporting && handleGoToIntegrationReporting()}>
                Integration Reports
              </Option>
            )}
          </OptionsDiv>
        )}
      </ContainerDiv>
    </OutsideEvent>
  );
};

const SystemConfigIntegrationCard: React.FC<SystemConfigIntegrationCardProps> = ({
  integration,
  isConnected,
  zoomIsEnabledForOrg,
  googleMeetIsEnabledForOrg,
  handleGoToManageTemplates,
  handleGoToIntegrationReporting,
  handleGoToSettingsPage,
  handleGoToFieldMapping,
}) => {
  const hasAnyOption = Object.values(integration?.options || {}).some((value) => value);
  const [switchStateIsConnected, setSwitchStateIsConnected] = useState(isConnected);

  const [
    updateOrgVideoIntegration,
    { loading: loadingUpdateOrgVideoIntegration, error: errorUpdateOrgVideoIntegration },
  ] = useMutation(UPDATE_ORG_VIDEO_INTEGRATION, {
    async onCompleted({ updateOrgVideoIntegration }) {
      appToast("Integration enabled successfully!");
    },
    onError({ message }) {
      appToast(message);
    },
    refetchQueries: ["checkIntegrationStatus"],
  });

  const systemConfigToggleOrgMeetingIntegration = async (type: "Google" | "Zoom") => {
    await updateOrgVideoIntegration({
      variables: {
        type,
      },
    });
  };

  const {
    showDisconnectIntegrationModal,
    setShowDisconnectIntegrationModal,
    selectedIntegrationDisconnect,
    setSelectedIntegrationDisconnect,
  } = useContext(ModalContext);

  const handleSwitchChange = async (checked: boolean) => {
    const organization_id = localStorage.getItem("organization_id");

    if (checked) {
      if (!ORG_INTEGRATIONS_THAT_NEED_OFF_SITE_PROCESSING.includes(integration.name)) {
        setSwitchStateIsConnected(true);
      }

      switch (integration.name) {
        case "Gmail":
          await systemConfigLinkGmail();
          break;
        case IIntegrations.HubSpot:
          await systemConfigLinkHubSpot();
          break;
        case IIntegrations.Zoom:
          await systemConfigLinkZoom(integration.url);
          break;
        case IIntegrations.GoogleMeet:
          await systemConfigToggleOrgMeetingIntegration("Google");
          break;
        case IIntegrations.GoogleCalendar:
        case IIntegrations.Outlook:
          await defaultLinkToSellfire(integration.url, organization_id);
          break;
        default:
          await defaultLinkToSellfire(integration.url, organization_id);
      }
      MixpanelActions.track("Integration Connected", { integration: integration.name });
    } else {
      setSelectedIntegrationDisconnect(integration.name);
      setShowDisconnectIntegrationModal(true);
    }
  };

  const timekitReplacementOrg = !!loggedInUser().organization?.replace_timekit;

  return (
    <Main isConnected={isConnected}>
      <Header>
        <IntegrationLogo
          src={integration.logo}
          width="32px"
          alt={`${getIntegrationLabel(integration.name, timekitReplacementOrg)} logo`}
        />
        <ConnectButton
          integration={integration}
          isConnected={switchStateIsConnected}
          disabled={systemConfigGetDisabledState({
            integration,
            switchStateIsConnected,
            loadingUpdateOrgVideoIntegration,
            timekitReplacementOrg,
            zoomIsEnabledForOrg,
            googleMeetIsEnabledForOrg,
          })}
          tooltipText={systemConfigGetTooltipText({
            integration,
            switchStateIsConnected,
            zoomIsEnabledForOrg,
            googleMeetIsEnabledForOrg,
            timekitReplacementOrg,
          })}
          onChange={handleSwitchChange}
        />
      </Header>
      <InfoSection integration={integration} timekitReplacementOrg={timekitReplacementOrg} />
      <FlexDiv
        gap={8}
        align="center"
        justify="space-between"
        style={{
          overflow: "visible",
          position: "relative",
          minHeight: "32px",
        }}
      >
        {hasAnyOption ? (
          <CreateSystemConfigIntegrationCardOptions
            integration={integration}
            handleGoToManageTemplates={handleGoToManageTemplates}
            handleGoToIntegrationReporting={handleGoToIntegrationReporting}
            handleGoToSettingsPage={handleGoToSettingsPage}
            handleGoToFieldMapping={handleGoToFieldMapping}
          />
        ) : null}
        {integration.learnMoreUrl && <LearnMoreButton integration={integration} />}
      </FlexDiv>
    </Main>
  );
};

const Main = styled.div<{ isConnected: boolean }>`
  width: 257px;
  border-radius: 8px;
  padding: 16px;
  border: ${({ isConnected }) =>
    isConnected ? `1px solid ${theme.border.neutral.primary}` : `1px solid ${theme.border.neutral.secondary}`};
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const IntegrationLogo = styled.img`
  display: flex;
  flex-direction: row;
`;

const OptionsDiv = styled.div`
  position: absolute;
  left: 0px;
  top: 40px;
  width: 171px;
  background-color: ${theme.WHITE_COLOR};
  border: 1px solid ${theme.NEUTRAL200};
  border-radius: 4px;
  z-index: 3;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  font-weight: normal;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0.2px;
  padding-left: 12px;
  background-color: ${theme.WHITE_COLOR};
  :first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }
  :last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  :hover {
    background-color: ${theme.NEUTRAL200};
  }
`;

const ContainerDiv = styled.div`
  cursor: pointer;
`;

export { SystemConfigIntegrationCard };
